import { Badge, Checkbox } from '@fyos/components';
import { ChevronUp, ChevronDown, MessageSquare, ThumbsUp } from 'lucide-react';
import type { Item, Category } from '@/lib/itemApi';
import type { ColumnDef } from './BoardColumn';

interface BoardListViewProps {
  items: Item[];
  columns: ColumnDef[];
  categories: Category[];
  onItemClick: (item: Item) => void;
  selectedItems?: Set<string>;
  onSelectItem?: (itemId: string, selected: boolean) => void;
  sortField: string;
  sortDir: 'asc' | 'desc';
  onSort: (field: string) => void;
}

const PRIORITY_ORDER: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'gray',
};

export default function BoardListView({
  items,
  columns,
  categories,
  onItemClick,
  selectedItems,
  onSelectItem,
  sortField,
  sortDir,
  onSort,
}: BoardListViewProps) {
  const getCategoryById = (categoryId?: string) =>
    categories.find(c => c.id === categoryId);

  const getColumnName = (columnSlug: string) =>
    columns.find(c => c.id === columnSlug)?.name || columnSlug;

  const sorted = [...items].sort((a, b) => {
    let cmp = 0;
    switch (sortField) {
      case 'title':
        cmp = a.title.localeCompare(b.title);
        break;
      case 'column':
        cmp = a.board_column.localeCompare(b.board_column);
        break;
      case 'priority':
        cmp = (PRIORITY_ORDER[a.priority || ''] || 0) - (PRIORITY_ORDER[b.priority || ''] || 0);
        break;
      case 'votes':
        cmp = (a.vote_count || 0) - (b.vote_count || 0);
        break;
      case 'created':
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      default:
        cmp = a.board_position - b.board_position;
    }
    return sortDir === 'desc' ? -cmp : cmp;
  });

  const SortHeader = ({ field, label, className }: { field: string; label: string; className?: string }) => (
    <button
      onClick={() => onSort(field)}
      className={`flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 ${className || ''}`}
    >
      {label}
      {sortField === field && (
        sortDir === 'asc'
          ? <ChevronUp className="h-3 w-3" />
          : <ChevronDown className="h-3 w-3" />
      )}
    </button>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            {onSelectItem && (
              <th className="w-8 px-3 py-2">
                <Checkbox
                  checked={selectedItems?.size === items.length && items.length > 0}
                  onChange={(e) => {
                    items.forEach(item => onSelectItem(item.id, e.target.checked));
                  }}
                  className="h-3.5 w-3.5"
                />
              </th>
            )}
            <th className="text-left px-3 py-2"><SortHeader field="title" label="Title" /></th>
            <th className="text-left px-3 py-2 hidden sm:table-cell"><SortHeader field="column" label="Column" /></th>
            <th className="text-left px-3 py-2 hidden md:table-cell">Category</th>
            <th className="text-left px-3 py-2 hidden lg:table-cell"><SortHeader field="priority" label="Priority" /></th>
            <th className="text-center px-3 py-2 hidden sm:table-cell"><SortHeader field="votes" label="Votes" /></th>
            <th className="text-right px-3 py-2 hidden md:table-cell"><SortHeader field="created" label="Created" /></th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-center py-12 text-gray-400 dark:text-gray-500">
                No items match your filters
              </td>
            </tr>
          ) : (
            sorted.map(item => {
              const category = getCategoryById(item.category_id);
              const isSelected = selectedItems?.has(item.id);

              return (
                <tr
                  key={item.id}
                  onClick={() => onItemClick(item)}
                  className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                    isSelected ? 'bg-brand-50 dark:bg-brand-950/20' : ''
                  }`}
                >
                  {onSelectItem && (
                    <td className="w-8 px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          onSelectItem(item.id, e.target.checked);
                        }}
                        className="h-3.5 w-3.5"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.title}
                      </span>
                      {item.item_state && item.item_state !== 'active' && (
                        <Badge
                          size="sm"
                          variant={item.item_state === 'blocked' ? 'error' : item.item_state === 'at_risk' ? 'warning' : 'gray'}
                        >
                          {item.item_state.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      {item.comment_count > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-400">
                          <MessageSquare className="h-3 w-3" />
                          {item.comment_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 hidden sm:table-cell">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">
                      {getColumnName(item.board_column)}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 hidden md:table-cell">
                    {category ? (
                      <Badge
                        size="sm"
                        style={{ backgroundColor: category.color + '20', color: category.color }}
                      >
                        {category.name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 hidden lg:table-cell">
                    {item.priority ? (
                      <Badge size="sm" variant={PRIORITY_COLORS[item.priority] as any}>
                        {item.priority}
                      </Badge>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center hidden sm:table-cell">
                    <span className="flex items-center justify-center gap-0.5 text-xs text-gray-500">
                      <ThumbsUp className="h-3 w-3" />
                      {item.vote_count || 0}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right hidden md:table-cell">
                    <span className="text-xs text-gray-400">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
